import * as React from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Stack,
  Dialog,
  Button,
  TextField,
  Typography,
  DialogContent,
} from "@mui/material";

import { addToast } from "../../../redux/slices/toastSlice";

import "./SignUpForm.scss";

export default function SignUpForm({ open, handleFormSubmit, isSubmitted }) {
  const dispatch = useDispatch();
  const [signUpForm, setSignUpForm] = React.useState({
    fullName: "",
    email: "",
    linkedinProfile: "",
  });

  // local validation state
  const [errors, setErrors] = React.useState({
    fullName: "",
    email: "",
    linkedinProfile: "",
  });

  // simple email regex (reasonable but not perfect)
  const isValidEmail = (value) => {
    if (!value) return false;
    // digit-by-digit style: conservative RFC-like check simplified
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(value.trim());
  };

  const isValidLinkedIn = (value) => {
    if (!value) return false;
    try {
      const u = new URL(value.trim());
      // require linkedin domain somewhere in hostname or pathname
      return (
        u.hostname.toLowerCase().includes("linkedin.com") ||
        u.href.toLowerCase().includes("linkedin.com")
      );
    } catch (e) {
      return false;
    }
  };

  const validateField = (name, value) => {
    switch (name) {
      case "fullName": {
        if (!value || String(value).trim().length === 0) {
          return "Full name is required.";
        }
        return "";
      }
      case "email": {
        if (!value || String(value).trim().length === 0) {
          return "Email is required.";
        }
        if (!isValidEmail(value)) {
          return "Please enter a valid email address.";
        }
        return "";
      }
      case "linkedinProfile": {
        if (!value || String(value).trim().length === 0) {
          return "LinkedIn profile link is required.";
        }
        if (!isValidLinkedIn(value)) {
          return "Please enter a valid LinkedIn profile URL.";
        }
        return "";
      }
      default:
        return "";
    }
  };

  const handleChangeForm = (e) => {
    const { name, value } = e.target;

    // update form
    setSignUpForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    // validate the field as user types
    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  // run a full validation, return boolean
  const validateAll = () => {
    const fullNameErr = validateField("fullName", signUpForm.fullName);
    const emailErr = validateField("email", signUpForm.email);
    const linkedInErr = validateField(
      "linkedinProfile",
      signUpForm.linkedinProfile
    );

    setErrors({
      fullName: fullNameErr,
      email: emailErr,
      linkedinProfile: linkedInErr,
    });

    return !(fullNameErr || emailErr || linkedInErr);
  };

  const handleFormSubmission = () => {
    // guard: validate
    if (!validateAll()) {
      dispatch(
        addToast({
          message: "Please fix the highlighted fields before submitting.",
          isSuccess: false,
        })
      );
      return;
    }

    // all good -> submit
    handleFormSubmit(signUpForm);
  };

  // derived flag for disabling submit
  const isFormValid =
    !validateField("fullName", signUpForm.fullName) &&
    !validateField("email", signUpForm.email) &&
    !validateField("linkedinProfile", signUpForm.linkedinProfile);

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      PaperProps={{
        className: "investigator-signup-dialog",
      }}
      slotProps={{
        backdrop: {
          className: "custom-backdrop-hint-signup",
        },
      }}
    >
      <DialogContent sx={{ px: 5, pt: 4, pb: 0 }}>
        {!isSubmitted ? (
          <React.Fragment>
            <Typography variant="h4" className="signup-title">
              Congratulations,
              <br />
              Investigator!
            </Typography>
            <Typography className="signup-subtitle">
              Provide your name and contact details to unlock
              <br />
              the full story and your personalised reward.
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                mb: 1,
              }}
            >
              <TextField
                variant="outlined"
                placeholder="Enter your full name"
                name="fullName"
                type="text"
                autoComplete="off"
                value={signUpForm.fullName}
                onChange={handleChangeForm}
                required
                autoFocus
                error={Boolean(errors.fullName)}
                helperText={errors.fullName || ""}
                inputProps={{ "aria-label": "Full name" }}
              />
              <TextField
                variant="outlined"
                placeholder="Enter your Email"
                name="email"
                type="email"
                autoComplete="off"
                value={signUpForm.email}
                onChange={handleChangeForm}
                required
                error={Boolean(errors.email)}
                helperText={errors.email || ""}
                inputProps={{ "aria-label": "Email address" }}
              />
              <TextField
                variant="outlined"
                placeholder="Link to your LinkedIn profile"
                name="linkedinProfile"
                type="text"
                autoComplete="off"
                value={signUpForm.linkedinProfile}
                onChange={handleChangeForm}
                required
                error={Boolean(errors.linkedinProfile)}
                helperText={errors.linkedinProfile || ""}
                inputProps={{ "aria-label": "LinkedIn profile URL" }}
              />
            </Box>

            <Stack direction="row" spacing={2}>
              <Button
                type="button"
                variant="contained"
                className="btn-flat"
                onClick={handleFormSubmission}
                disabled={!isFormValid}
              >
                Submit
              </Button>
            </Stack>
          </React.Fragment>
        ) : (
          <Stack>
            <Typography
              variant="h4"
              className="signup-title"
              sx={{ textAlign: "center", mb: 3 }}
            >
              Success
            </Typography>
            <Typography className="signup-subtitle">
              Transmission received. Your details have been logged. We'll be in
              contact soon.
            </Typography>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
